import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { CountableTimeZonePipe } from 'src/app/helpers/countable-timezone.pipe';
import { TimezoneService } from 'src/app/services/timezone.service';

export const MY_FORMATS = {
  parse: {
    dateInput: "MM/DD/YYYY"
  },
  display: {
    dateInput: "MM/DD/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "YYYY-MM-DD HH:mm:ss",
    monthYearA11yLabel: "MMMM YYYY"
  }
};

@Component({
  selector: 'app-countable-date',
  imports: [CommonModule, MatDatepickerModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatTooltipModule, CountableTimeZonePipe],
  templateUrl: './countable-mat-date.component.html',
  styleUrl: './countable-mat-date.component.scss',
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    CountableTimeZonePipe
  ]
})
export class CountableMatDateComponent implements OnInit {
  private _inputDate: Date | string = null;
  private _isMinDate: boolean = false;
  private _isMaxDate: boolean = false;
  private _inputMinDate: Date | string = null;
  private _inputMaxDate: Date | string = null;
  private _label: string = null;
  private _subLabel: string = null;
  private _placeholderText: string = null;
  private _isMatActions: boolean = false;
  private _isReadOnly: boolean = false;
  private _isDisabled: boolean = false;
  private _showDateAndFormat: boolean = false;
  private _isRequired: boolean = false;
  private _showInfo: boolean = false;
  private _checkCondition: string = null;
  public displayLabel: string;

  @Input()
  set inputDate(value: Date | string) {
    this._inputDate = value;
    this.onChangeInputDate();
  }

  get inputDate() {
    return this._inputDate;
  }

  @Input()
  set isMinDate(value: boolean) {
    this._isMinDate = value;
    this.onChangeInputMinDate();
  }

  get isMinDate() {
    return this._isMinDate;
  }

  @Input()
  set isMaxDate(value: boolean) {
    this._isMaxDate = value;
    this.onChangeInputMaxDate();
  }

  get isMaxDate() {
    return this._isMaxDate;
  }

  @Input()
  set inputMaxDate(value: Date | string) {
    this._inputMaxDate = value;
    this.onChangeInputMaxDate();
  }

  get inputMaxDate() {
    return this._inputMaxDate;
  }

  @Input()
  set inputMinDate(value: Date | string) {
    this._inputMinDate = value;
    this.onChangeInputMinDate();
  }

  get inputMinDate() {
    return this._inputMinDate;
  }

  @Input()
  set label(value: string) {
    this._label = value;
  }

  get label() {
    return this._label;
  }

  @Input()
  set subLabel(value: string) {
    this._subLabel = value;
  }

  get subLabel() {
    return this._subLabel;
  }

  @Input()
  set placeholderText(value: string) {
    this._placeholderText = value;
  }

  get placeholderText() {
    return this._placeholderText;
  }

  @Input()
  set isMatActions(value: boolean) {
    this._isMatActions = value;
  }

  get isMatActions() {
    return this._isMatActions;
  }

  @Input()
  set isReadOnly(value: boolean) {
    this._isReadOnly = value;
  }

  get isReadOnly() {
    return this._isReadOnly;
  }

  @Input()
  set isDisabled(value: boolean) {
    this._isDisabled = value;
    this._isDisabled ? this.localDate.disable() : this.localDate.enable();
    this.localDate.updateValueAndValidity();
  }

  get isDisabled() {
    return this._isDisabled;
  }

  @Input()
  set showDateAndFormat(value: boolean) {
    this._showDateAndFormat = value;
  }

  get showDateAndFormat() {
    return this._showDateAndFormat;
  }

  @Input()
  set isRequired(value: boolean) {
    this._isRequired = value;
    this._isRequired ? this.localDate.setValidators([Validators.required]) : this.localDate.clearValidators();
    this.localDate.updateValueAndValidity();
  }

  get isRequired() {
    return this._isRequired;
  }

  @Input()
  set showInfo(value: boolean) {
    this._showInfo = value;
  }

  get showInfo() {
    return this._showInfo;
  }

  @Input()
  set checkCondition(value: string) {
    this._checkCondition = value;
    this.onChangeInputDate();
  }

  get checkCondition() {
    return this._checkCondition;
  }

  @Output() selectedDate = new EventEmitter();
  localDate: FormControl<any> = new FormControl;
  maxDate: FormControl<any> = new FormControl;
  minDate: FormControl<any> = new FormControl;
  utcDate: string;
  currentDate = this.timezoneService.getCurrentUTCDate();

  constructor(private timezoneService: TimezoneService, private toastr: ToastrService) { }

  ngOnInit(): void {

  }

  private onChangeInputDate() {
    // value  for mat date picker
    if (this.timezoneService.validateDateInput(this.inputDate)) {
      if (typeof this.inputDate === 'string') {
        const formatt = this.parseDate(this.inputDate);
        this.localDate.setValue(formatt);
        this.displayLabel = formatt.split('T')[0];
      } else {
        this.localDate.setValue(this.timezoneService.toUTC(this.inputDate));
        this.displayLabel = this.timezoneService.toUTC(this.inputDate);
      }

      if (this._checkCondition === 'dueDateIssues') {
        if (this.timezoneService.diffInDays(this.localDate.value, this.currentDate) < 0) {
          this.localDate.markAsTouched();
          this.selectedDate.emit('error');
        } else {
          this.localDate.markAsUntouched();
        }
      }
    } else {
      this.localDate.setValue(null);
    }
  }

  private onChangeInputMaxDate() {
    if (this.isMaxDate) {
      if (this.timezoneService.validateDateInput(this.inputMaxDate)) {
        if (typeof this.inputMaxDate === 'string') {
          const formatt = this.parseDate(this.inputMaxDate);
          this.maxDate.setValue(formatt);
        } else {
          this.maxDate.setValue(this.timezoneService.toUTC(this.inputMaxDate));
        }
      } else {
        this.maxDate.setValue(this.currentDate);
      }
    } else {
      this.maxDate.setValue(null);
    }
  }

  private onChangeInputMinDate() {
    if (this.isMinDate) {
      if (this.timezoneService.validateDateInput(this.inputMinDate)) {
        if (typeof this.inputMinDate === 'string') {
          const formatt = this.parseDate(this.inputMinDate);
          this.minDate.setValue(formatt);
        } else {
          this.minDate.setValue(this.timezoneService.toUTC(this.inputMinDate));
        }
      } else {
        this.minDate.setValue(this.currentDate);
      }
    } else {
      this.minDate.setValue(null);
    }
  }

  public onDateChange(date: any): void {
    if (this.localDate.status === 'VALID' && this.localDate.value) {
      const adjustedDate = new Date(Date.UTC(moment(this.localDate.value).year(), moment(this.localDate.value).month(), moment(this.localDate.value).date()));
      this.utcDate = this.timezoneService.toUTC(adjustedDate);
      this.selectedDate.emit(this.utcDate);
    } else {
      this.toastr.error('Please enter valid date in MM/DD/YYYY format');
      this.utcDate = null;
      this.selectedDate.emit(null);
    }
    this.displayLabel = this.utcDate;
  }

  private parseDate(inputValue): string {
    let dateUTC;

    // Define patterns for different date formats
    const mmddyyyyPattern = /^(0[1-9]|1[0-2])[\/-](0[1-9]|[12][0-9]|3[01])[\/-](\d{4})$/; // MM/DD/YYYY or MM-DD-YYYY
    const yyyymmddPattern = /^(\d{4})[\/-](0[1-9]|1[0-2])[\/-](0[1-9]|[12][0-9]|3[01])$/; // YYYY-MM-DD or YYYY/MM/DD
    const ddmmyyyyPattern = /^(0[1-9]|[12][0-9]|3[01])[\/-](0[1-9]|1[0-2])[\/-](\d{4})$/; // DD/MM/YYYY or DD-MM-YYYY

    // Check for MM/DD/YYYY or MM-DD-YYYY
    if (mmddyyyyPattern.test(inputValue)) {
      const [month, day, year] = inputValue.split(/\/|-/).map(Number);
      const monthPad = String(month).padStart(2, '0');
      const dayPad = String(day).padStart(2, '0');
      dateUTC = `${year}-${monthPad}-${dayPad}T00:00:00Z`;
    }
    // Check for YYYY-MM-DD or YYYY/MM/DD
    else if (yyyymmddPattern.test(inputValue)) {
      const [year, month, day] = inputValue.split(/\/|-/).map(Number);
      const monthPad = String(month).padStart(2, '0');
      const dayPad = String(day).padStart(2, '0');
      dateUTC = `${year}-${monthPad}-${dayPad}T00:00:00Z`;
    }
    // Check for DD/MM/YYYY or DD-MM-YYYY
    else if (ddmmyyyyPattern.test(inputValue)) {
      const [day, month, year] = inputValue.split(/\/|-/).map(Number);
      const monthPad = String(month).padStart(2, '0');
      const dayPad = String(day).padStart(2, '0');
      dateUTC = `${year}-${monthPad}-${dayPad}T00:00:00Z`;
    }
    // Fallback to default date parsing
    else {
      dateUTC = this.formatDateToUTC(new Date(inputValue));

    }

    return dateUTC;
  }

  private formatDateToUTC(date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00Z`;
  }

}
